import React, { useState } from 'react';
import _get from 'lodash/get';
import { Attachment, TaskCompletion } from 'graphql-common';
import { useTaskTimelineFormAnswerMediaByIdsLazyQuery } from '@lib/graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import StatusBox from '@lib/components/StatusBox/StatusBox';
import ValidityHint from '@lib/enums/validityHint';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getCompletionMediaIds from './getCompletionMediaIds';
import styles from './TaskCompletionMediaCell.module.scss';

type Props = {
  completion: TaskCompletion;
  groupKey: string;
};

// Ids are already known from the row's own answers, so all of a completion's
// media is fetched in one shot here and navigated locally — unlike the
// task-wide media grid (see useTaskCompletionsMediaViewer), there is no
// pagination to fetch incrementally against.
export default function TaskCompletionMediaCell({
  completion,
  groupKey,
}: Props) {
  const mediaIds = getCompletionMediaIds(completion.answers);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const [fetchMedia, { loading }] =
    useTaskTimelineFormAnswerMediaByIdsLazyQuery();

  if (!mediaIds.length) {
    return <TextCell value="-" item={completion} />;
  }

  const handleOpen = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (attachments.length) {
      setActiveIndex(0);
      setIsOpen(true);
      return;
    }
    const { data } = await fetchMedia({
      variables: { groupKey, ids: mediaIds },
    });
    const byId = new Map(
      (_get(data, 'data.collection', []) as Attachment[]).map((item) => [
        item.id,
        item,
      ]),
    );
    const ordered = mediaIds
      .map((id) => byId.get(id))
      .filter((item): item is Attachment => !!item);
    setAttachments(ordered);
    setActiveIndex(0);
    setIsOpen(true);
  };

  const activeAttachment = attachments[activeIndex];

  return (
    // Stops propagation on bubble so a click anywhere in this subtree never
    // reaches the row's onRowClick — including clicks inside FileViewerModal,
    // which portals into document.body but still bubbles through the React
    // tree (and therefore through this wrapper) rather than the DOM tree.
    <div
      role="presentation"
      className={styles.mediaCellContent}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={styles.mediaBadgeButton}
        onClick={handleOpen}
        disabled={loading}
      >
        <StatusBox type={ValidityHint.None} text={mediaIds.length} />
      </button>
      {isOpen && activeAttachment && (
        <FileViewerModal
          attachment={activeAttachment}
          attachmentName={activeAttachment.filename}
          isOpen
          onClose={() => setIsOpen(false)}
          swiperProps={{
            onPrevious: () => setActiveIndex((index) => index - 1),
            onNext: () => setActiveIndex((index) => index + 1),
            isPreviousDisabled: activeIndex <= 0,
            isNextDisabled: activeIndex >= attachments.length - 1,
          }}
        />
      )}
    </div>
  );
}
